// src/pages/Gallery.js
import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useAuth } from '../AuthProvider';
import '../styles/BellaBizarreStyle.css';
import ImageLightbox from '../components/ImageLightbox';

export default function Gallery() {
    const [mediaItems, setMediaItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const { user } = useAuth();

    // 'none' | 'date' | 'category'
    const [organizeBy, setOrganizeBy] = useState('category');

    // Lightbox
    const [lightbox, setLightbox] = useState(null);
    const openLightbox = (src, alt = '') => setLightbox({ src, alt });
    const closeLightbox = () => setLightbox(null);

    useEffect(() => {
        (async () => {
            try {
                const snap = await getDocs(collection(db, 'media'));
                setMediaItems(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            } catch (e) {
                console.error('Error fetching media:', e);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this media?')) return;
        try {
            await deleteDoc(doc(db, 'media', id));
            setMediaItems(cur => cur.filter(x => x.id !== id));
        } catch (e) {
            console.error('Error deleting media:', e);
        }
    };

    // ---- helpers ----
    const toDateVal = (item) => {
        const d = item?.date ?? item?.createdAt;
        if (!d) return 0;
        if (d.toDate) return d.toDate().getTime();
        const t = new Date(d).getTime();
        return Number.isNaN(t) ? 0 : t;
    };

    const sortedByDate = useMemo(
        () => [...mediaItems].sort((a, b) => toDateVal(b) - toDateVal(a)),
        [mediaItems]
    );

    const groupedByCategory = useMemo(() => {
        const groups = new Map();
        mediaItems.forEach(item => {
            const cats = Array.isArray(item.categories) && item.categories.length
                ? item.categories
                : ['Uncategorized'];
            cats.forEach(cat => {
                if (!groups.has(cat)) groups.set(cat, []);
                groups.get(cat).push(item);
            });
        });
        groups.forEach(arr => arr.sort((a, b) => toDateVal(b) - toDateVal(a)));
        return Array.from(groups.entries()).sort((a, b) => a[0].localeCompare(b[0]));
    }, [mediaItems]);

    const Card = ({ item }) => (
        <article className="gallery-item fade-in">
            {item.mediaType === 'image' && (
                <img
                    src={item.url}
                    alt={item.name || 'Artwork'}
                    className="gallery-image"
                    onClick={() => openLightbox(item.url, item.name)}
                />
            )}

            {item.mediaType === 'video' && (
                <video controls src={item.url} className="gallery-video" />
            )}

            {item.mediaType === 'embed' && (
                <div className="embed-wrapper">
                    <iframe
                        src={item.url}
                        title={item.name || 'Embedded media'}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                    />
                </div>
            )}

            {item.name && <h3 className="gallery-title">{item.name}</h3>}
            {item.description && <p className="gallery-desc">{item.description}</p>}

            {Array.isArray(item.categories) && item.categories.length > 0 && (
                <p className="gallery-meta">
                    <strong>Categories:</strong> {item.categories.join(', ')}
                </p>
            )}

            {item.date && (
                <p className="gallery-meta">
                    <strong>Date:</strong>{' '}
                    {item.date?.toDate ? item.date.toDate().toLocaleDateString() : String(item.date)}
                </p>
            )}

            {user && item.ownerId === user.uid && (
                <button onClick={() => handleDelete(item.id)} className="gallery-delete-button">
                    Delete
                </button>
            )}
        </article>
    );

    const Grid = ({ items }) => (
        <div className="gallery-container">
            {items.length ? items.map((item) => <Card key={item.id} item={item} />) : <p>No media yet.</p>}
        </div>
    );

    if (loading) return <p>Loading gallery…</p>;

    return (
        <div className="outer-gallery-container">
            <h1>Bella Bizarre Gallery</h1>

            <div className="gallery-toolbar">
                <label>
                    Organize by:{' '}
                    <select value={organizeBy} onChange={(e) => setOrganizeBy(e.target.value)}>
                        <option value="none">None</option>
                        <option value="date">Date</option>
                        <option value="category">Category</option>
                    </select>
                </label>
            </div>

            {organizeBy === 'none' && <Grid items={mediaItems} />}

            {organizeBy === 'date' && <Grid items={sortedByDate} />}

            {organizeBy === 'category' && (
                <>
                    {groupedByCategory.length === 0 && <p>No media yet.</p>}
                    {groupedByCategory.map(([cat, items]) => (
                        <section key={cat} className="gallery-section">
                            <h2 className="gallery-section-title">{cat}</h2>
                            <Grid items={items} />
                        </section>
                    ))}
                </>
            )}

            {lightbox && (
                <ImageLightbox src={lightbox.src} alt={lightbox.alt} onClose={closeLightbox} />
            )}
        </div>
    );
}
